/**
 * 사용자 상세 — 판촉영업 위탁 계약서 탭
 * 레거시 `userDetailInfo.js` consignment 섹션 UI·검토 저장 로직 포팅.
 * 저장은 부모(UserDetailPage) 단일 저장 버튼에서 수행한다.
 */
import type { PlatformUserDetailResponse } from '@/entities/user/api/user.api';
import {
  useApplyConsignmentReview,
  useConsignmentContractTab,
  useContractSignedHtml,
} from '@/entities/contract/hooks/useConsignmentContract';
import { ContractPdfPreview } from '@/pages/users/components/ContractPdfPreview';
import { SignedHtmlContractPreview } from '@/pages/users/components/SignedHtmlContractPreview';
import {
  CONSIGNMENT_ADMIN_REVIEW_STATUS,
  CONSIGNMENT_RECONTRACT_REASON_OPTIONS,
  buildConsignmentAdminReviewStatusSelectOptions,
  formatContractDateTimeDisplay,
  getContractFinalPdfDownloadUrlFromDetail,
  getContractSignedAtFromDetail,
  getSignedHtmlContentFromContractDetail,
  getSignedHtmlDownloadUrlFromContractDetail,
  getSignedHtmlInlineUrlFromContractDetail,
  hasConsignmentContractDownloadSource,
  hasReplacementContractStarted,
  isConsignmentContractReviewPending,
  isContractCompletedFromInfo,
  isExternalContractFromInfo,
  mapApiReviewStatusToAdmin,
  resolveContractIdFromInfo,
  shouldFetchContractSignedHtml,
  shouldShowConsignmentAdminReviewForm,
  type ConsignmentAdminReviewStatus,
} from '@/pages/users/consignmentContractUtils';
import { ContractStatusTag } from '@/pages/users/UserStatusTags';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { handleApiError, useToast } from '@/shared/utils/toast';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface ConsignmentTabHandle {
  isDirty: () => boolean;
  save: () => Promise<boolean>;
}

interface UserConsignmentContractTabProps {
  user: PlatformUserDetailResponse;
  /** 위탁 계약 탭이 보일 때만 서명 HTML을 요청한다 */
  active?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  onSaved?: () => void;
}

export const UserConsignmentContractTab = forwardRef<
  ConsignmentTabHandle,
  UserConsignmentContractTabProps
>(({ user, active = false, onDirtyChange, onSaved }, ref) => {
  const toast = useToast();
  const contractInfo = user.contractInfo ?? null;
  const enabled = contractInfo != null;

  const { data, isLoading, isError, error, refetch } = useConsignmentContractTab(
    {
      enabled,
      userId: user.userId,
      email: user.email,
      contractInfo,
    },
  );

  const applyReview = useApplyConsignmentReview();

  const contractDetail = data?.contractDetail ?? null;
  const resolvedContractId =
    data?.resolvedContractId ??
    resolveContractIdFromInfo(contractInfo, data?.matchedPendingRow);

  const isReviewPending = isConsignmentContractReviewPending(
    contractInfo,
    contractDetail,
  );
  const shouldFetchHtml = shouldFetchContractSignedHtml({
    active,
    isTabLoading: isLoading,
    isReviewPending,
    contractId: resolvedContractId,
    contractDetail,
  });
  const signedHtmlQuery = useContractSignedHtml(
    resolvedContractId,
    shouldFetchHtml,
  );

  const [reviewStatus, setReviewStatus] = useState<ConsignmentAdminReviewStatus>(
    CONSIGNMENT_ADMIN_REVIEW_STATUS.BEFORE_CHECK,
  );
  const [recontractReason, setRecontractReason] = useState('');
  const [baselineReviewStatus, setBaselineReviewStatus] =
    useState<ConsignmentAdminReviewStatus>(
      CONSIGNMENT_ADMIN_REVIEW_STATUS.BEFORE_CHECK,
    );
  const [baselineRecontractReason, setBaselineRecontractReason] = useState('');
  const [reviewInitialized, setReviewInitialized] = useState(false);
  const initializedUserIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (initializedUserIdRef.current !== user.userId) {
      initializedUserIdRef.current = user.userId;
      setReviewInitialized(false);
    }
  }, [user.userId]);

  useEffect(() => {
    if (!enabled || isLoading || reviewInitialized) return;
    const initialReview =
      mapApiReviewStatusToAdmin(
        contractDetail?.reviewStatus ?? contractInfo?.reviewStatus,
      ) ?? CONSIGNMENT_ADMIN_REVIEW_STATUS.BEFORE_CHECK;
    const initialReason =
      contractDetail?.recontractRequestReason ??
      contractInfo?.recontractRequestReason ??
      '';
    setReviewStatus(initialReview);
    setRecontractReason(initialReason);
    setBaselineReviewStatus(initialReview);
    setBaselineRecontractReason(initialReason);
    setReviewInitialized(true);
  }, [enabled, isLoading, contractDetail, contractInfo, reviewInitialized]);

  const showReviewForm = shouldShowConsignmentAdminReviewForm(
    contractInfo,
    contractDetail,
  );
  const replacementStarted = hasReplacementContractStarted(contractDetail);
  const isCompleted = isContractCompletedFromInfo(contractInfo);
  const isExternalContract = isExternalContractFromInfo(contractInfo);

  const reviewOptions = useMemo(
    () =>
      buildConsignmentAdminReviewStatusSelectOptions({
        replacementStarted,
        currentReviewKey:
          contractDetail?.reviewStatus ?? contractInfo?.reviewStatus,
      }),
    [replacementStarted, contractDetail, contractInfo],
  );

  const contractAtDisplay = formatContractDateTimeDisplay(
    getContractSignedAtFromDetail(contractDetail) ??
      contractInfo?.contractedAt ??
      user.contractCompletedAt,
  );
  const signedAtDisplay = formatContractDateTimeDisplay(
    getContractSignedAtFromDetail(contractDetail) ?? contractInfo?.updatedAt,
  );
  const documentNumber =
    contractDetail?.documentNumber ??
    contractInfo?.documentNumber ??
    user.contractDocumentNumber ??
    '';

  const signedHtmlDownloadUrl =
    getSignedHtmlDownloadUrlFromContractDetail(contractDetail);
  const signedHtmlInlineUrl =
    getSignedHtmlInlineUrlFromContractDetail(contractDetail);
  const adminHtmlContent =
    getSignedHtmlContentFromContractDetail(contractDetail) ??
    signedHtmlQuery.data ??
    null;
  const hasSignedHtmlPreviewSource = Boolean(
    adminHtmlContent || signedHtmlInlineUrl || signedHtmlDownloadUrl,
  );

  const finalPdfDownloadUrl = getContractFinalPdfDownloadUrlFromDetail(
    contractDetail,
    contractInfo,
  );
  const showPdfPreview =
    !isReviewPending &&
    (isCompleted ||
      hasConsignmentContractDownloadSource(contractInfo, finalPdfDownloadUrl));

  const hasFormChanges =
    reviewInitialized &&
    showReviewForm &&
    (reviewStatus !== baselineReviewStatus ||
      recontractReason !== baselineRecontractReason);

  useEffect(() => {
    onDirtyChange?.(hasFormChanges);
  }, [hasFormChanges, onDirtyChange]);

  useEffect(() => {
    if (!enabled) {
      onDirtyChange?.(false);
    }
  }, [enabled, onDirtyChange]);

  const saveReview = async (): Promise<boolean> => {
    if (!hasFormChanges) return true;
    if (!resolvedContractId) {
      toast.error('계약 ID를 찾을 수 없습니다.');
      return false;
    }
    if (
      reviewStatus === CONSIGNMENT_ADMIN_REVIEW_STATUS.RECONTRACT_REQUEST &&
      !recontractReason
    ) {
      toast.error('재계약 사유를 선택해 주세요.');
      return false;
    }
    if (
      replacementStarted &&
      reviewStatus === CONSIGNMENT_ADMIN_REVIEW_STATUS.COMPLETE
    ) {
      toast.error(
        '이미 후속 재계약이 시작되어 기존 계약의 검토 상태를 변경할 수 없습니다.',
      );
      return false;
    }

    try {
      await applyReview.mutateAsync({
        contractId: resolvedContractId,
        reviewStatusUi: reviewStatus,
        recontractReason: recontractReason || undefined,
        contractDetail,
        contractInfo,
      });
      setBaselineReviewStatus(reviewStatus);
      setBaselineRecontractReason(recontractReason);
      await refetch();
      onSaved?.();
      return true;
    } catch (e) {
      handleApiError(e, toast);
      return false;
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      isDirty: () => hasFormChanges,
      save: saveReview,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasFormChanges, reviewStatus, recontractReason, resolvedContractId],
  );

  if (!enabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">판촉영업 위탁 계약서</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="py-8 text-center text-sm text-muted-foreground">
            계약 정보가 없습니다.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center gap-3 space-y-0">
        <CardTitle className="text-base">판촉영업 위탁 계약서</CardTitle>
        <ContractStatusTag record={{ contractInfo }} />
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <LoadingSpinner tip="위탁 계약 정보를 불러오는 중..." />
        ) : null}
        {isError && error ? (
          <p className="text-sm text-destructive">
            계약 정보 조회 실패: {String(error)}
          </p>
        ) : null}

        {/* 레거시: 서명 검토 중 — 계약 일시·문서번호·서명 HTML */}
        {isReviewPending ? (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label>계약 일시</Label>
                <Input disabled value={signedAtDisplay} />
              </div>
              <div className="space-y-1">
                <Label>문서번호</Label>
                <Input disabled value={documentNumber || '-'} />
              </div>
            </div>
            {signedHtmlQuery.isFetching ? (
              <LoadingSpinner tip="서명 HTML을 불러오는 중..." />
            ) : hasSignedHtmlPreviewSource ? (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">서명 HTML</h4>
                <SignedHtmlContractPreview
                  downloadUrl={signedHtmlDownloadUrl}
                  inlineUrl={signedHtmlInlineUrl}
                  htmlContent={adminHtmlContent}
                  title="서명된 위탁 계약 HTML"
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                서명 HTML 미리보기를 불러올 수 없습니다.
              </p>
            )}
          </div>
        ) : null}

        {/* 서명 HTML이 아닌 확정 문서 — 외부 계약 사용자도 contractId/URL이 있으면 노출 */}
        {showPdfPreview ? (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label>계약 일시</Label>
                <Input disabled value={contractAtDisplay} />
              </div>
              <div className="space-y-1">
                <Label>문서번호</Label>
                <Input disabled value={documentNumber || '-'} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>판촉 영업 위탁 계약서</Label>
              <ContractPdfPreview
                contractId={resolvedContractId}
                finalPdfDownloadUrl={finalPdfDownloadUrl}
                downloadName={`${user.companyName || 'user'}_판촉영업_위탁_계약서`}
              />
            </div>
          </div>
        ) : !showReviewForm ? (
          <p className="text-sm text-muted-foreground">
            {isExternalContract
              ? '외부 계약으로 등록되어 있습니다. 루다링크 계약 문서가 생성되면 검토 상태가 표시됩니다.'
              : '계약이 완료되면 상세 정보가 표시됩니다.'}
          </p>
        ) : null}

        {showReviewForm ? (
          <div className="space-y-4 border-t pt-4">
            {replacementStarted ? (
              <p className="text-sm text-muted-foreground">
                후속 재계약 DRAFT가 생성되었습니다. 동일 사유로 재저장해도 후속
                계약은 중복 생성되지 않으며, 계약 완료 상태로 되돌릴 수
                없습니다.
              </p>
            ) : null}
            <div
              className={`grid gap-4 ${
                reviewStatus ===
                CONSIGNMENT_ADMIN_REVIEW_STATUS.RECONTRACT_REQUEST
                  ? 'md:grid-cols-2'
                  : 'md:grid-cols-1'
              }`}
            >
              <div className="space-y-1">
                <Label>상태</Label>
                <Select
                  value={reviewStatus}
                  onValueChange={(v) =>
                    setReviewStatus(v as ConsignmentAdminReviewStatus)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="상태 선택" />
                  </SelectTrigger>
                  <SelectContent>
                    {reviewOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {reviewStatus ===
              CONSIGNMENT_ADMIN_REVIEW_STATUS.RECONTRACT_REQUEST ? (
                <div className="space-y-1">
                  <Label>사유</Label>
                  <Select
                    value={recontractReason}
                    onValueChange={setRecontractReason}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="사유 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONSIGNMENT_RECONTRACT_REASON_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
});

UserConsignmentContractTab.displayName = 'UserConsignmentContractTab';
